import { Component, computed, input } from '@angular/core';
import { MatTooltip, MatTooltipModule } from '@angular/material/tooltip';
import { BillingTaskHours } from '../../../models/billing-history.models';

/**
 * Stand-ins for the two ways the feed can fail to give us a value. They mean different things
 * and are worded differently on purpose: null is the field being absent, blank is the field
 * being present and carrying nothing.
 */
const NULL_PLACEHOLDER = 'Not Provided';
const BLANK_PLACEHOLDER = 'Unspecified';

/** The glyph standing in for a code we do not have. Matches the billing card's own "nothing". */
const NO_CODE = '–';

/** True when a value is present but holds no text. Null is absent, which is a different case. */
function isBlank(value: string | null | undefined): boolean {
  return value != null && value.trim() === '';
}

/** One rendered line of the breakdown: a single task bucket. */
interface TaskBreakdownRow {
  label: string;
  description: string;
  /**
   * The feed's figure as the API returns it, already rounded to one decimal, shown as is. Not
   * run through `formatBillingHours`: that exists for scope totals, where it renders 0 as an
   * en dash meaning "none this period". A task
   * bucket is only in the list because the feed reported it, so displaying it as nothing would
   * be a false claim — and the dash would collide with the untasked marker in the code column.
   */
  hours: number;
  /** No usable code, so the label is the stand-in glyph rather than a real value. */
  hasNoCode: boolean;
  /** The description is a stand-in rather than text the feed sent. */
  hasPlaceholderDescription: boolean;
}

/**
 * The task-code breakdown shown when a billing-history row is expanded. Shared by the resource
 * and matter billing cards so both perspectives read identically.
 *
 * Shows only what the feed reported: a line per coded bucket, plus the untasked bucket (a null
 * `taskCode` — time the feed reported as having no code). These do not necessarily add up to the
 * row's total: the feed guarantees only that task hours sum to *at most* it, and rounding each
 * bucket to one decimal can push them slightly over. Neither gap is surfaced.
 */
@Component({
  selector: 'app-task-breakdown',
  standalone: true,
  imports: [MatTooltipModule],
  templateUrl: './task-breakdown.component.html',
  styleUrls: ['./task-breakdown.component.scss'],
})
export class TaskBreakdownComponent {
  /**
   * The row's own task buckets. `null`/`undefined` means the feed supplied no task data, which
   * is NOT the same as an empty array (it supplied it, and there was no task time).
   */
  readonly tasks = input<BillingTaskHours[] | null | undefined>(null);

  /** True when the feed said nothing at all about this row's task hours. */
  readonly hasNoData = computed(() => this.tasks() == null);

  /**
   * True when the feed supplied the field but reported no task time — never for a null, which
   * is {@link hasNoData}. No `?? 0` here: that would make a null length compare equal to 0 and
   * report "no task time recorded" for a row the feed said nothing about, which is the one
   * claim this component exists to avoid making. Correct today only because the template tests
   * {@link hasNoData} first, and that ordering is not something to depend on.
   */
  readonly isEmpty = computed(() => this.tasks()?.length === 0);

  /**
   * Enables the tooltip only when the description is actually clipped, so a fully visible one
   * does not get a tooltip repeating what is already on screen. Same approach as the request
   * list's description column.
   */
  updateTooltipDisabled(el: HTMLElement, tooltip: MatTooltip): void {
    tooltip.disabled = el.scrollWidth <= el.clientWidth;
  }

  readonly rows = computed<TaskBreakdownRow[]>(() => {
    const tasks = this.tasks();
    if (!tasks?.length) {
      return [];
    }

    // Each field stands in for itself: an absent value reads "Not Provided", a present but
    // empty one reads "Unspecified". A missing code is shown as a glyph rather than words,
    // since the code column is narrow and the description carries the explanation.
    return tasks.map((task) => {
      const hasNoCode = task.taskCode == null || isBlank(task.taskCode);
      const hasPlaceholderDescription =
        task.taskDescription == null || isBlank(task.taskDescription);

      return {
        label: hasNoCode ? NO_CODE : task.taskCode!,
        description:
          task.taskDescription == null
            ? NULL_PLACEHOLDER
            : isBlank(task.taskDescription)
              ? BLANK_PLACEHOLDER
              : task.taskDescription,
        hours: task.totalHours,
        hasNoCode,
        hasPlaceholderDescription,
      };
    });
  });
}
